import React, {useCallback, useContext, useMemo, useRef, useState} from 'react';
import {CSSTransition} from 'react-transition-group';
import flow from 'lodash.flow';
import debounce from 'lodash.debounce';
import TitledName from "../TitledName/TitledName";
import ActionsList, {AXIS_VERTICAL, TYPE_SECONDARY} from "../ActionsList/ActionsList";
import AppContext, {ACTION_ISOLATE_ITEM_DISABLED, INTRO_MOVIE_FINISHED} from "../../store";
import ConfiguratorHolder from './ConfiguratorHolder';
import IconClose from "../IconButton/IconClose/IconClose";
import {
	PROPERTY_ACTIVE,
	PROPERTY_VISIBLE,
	PROPERTY_CLASS_NAME,
	PREFIX_EFFECT,
} from "../../contants/common";
import {
	applyIconDataNew,
	applyEvents,
	applyProp,
	applyProperties,
} from "./helpers";
import EventsCollection from '../../helpers/EventsCollection';
import {
	ACTION_RGB,
	ACTION_FULL_SCREEN,
	ACTION_RESET_CAMERA,
	ACTION_POWER,
	ACTION_OPEN,
	ACTION_INSPECT,
	PRESET_RAIN,
	PRESET_COLOR_ONE,
	PRESET_COLOR_TWO,
	PRESET_COLOR_THREE,
	PRESET_FULL_SCREEN,
	PRESET_RGB,
	PRESET_POWER,
	PRESET_OPEN,
	PRESET_INSPECT,
	PRESET_RAINBOW,
	PRESET_RESET_CONFIG,
	PRESET_RESET_CAMERA,
	SYSTEM_ID_ICUE,
	SYSTEM_ID_RGB,
	ACTION_RESET_CONFIG,
	SYSTEM_ID_TOGGLE_POWER,
	SYSTEM_ID_TOGGLE_DOOR,
	SYSTEM_ID_TOGGLE_INSPECT,
	PRESET_INFO,
	PRESET_STUDIO_MODE,
	SYSTEM_ID_INFO,
	EFFECT_ID_RAINBOW,
	EFFECT_ID_RAIN, ACTION_STUDIO_MODE, ACTION_CALL_INFO, SYSTEM_ID_TOGGLE_DESK_CAMERA,
	PRESET_SOUND,
	ACTION_SOUND,
	SYSTEM_ID_SOUND,
} from "../../contants/presets";
import useConfiguratorMount from "./hooks/useConfiguratorMount";
import {openFullscreen} from "../../helpers";
import classNames from "classnames";
import useViewMode from "../../hooks/useViewMode";
import useStreamActionsSingle from "../../hooks/useStreamActionsSingle";
import useSound from "./hooks/useSound";

const actionsInfo = [
	PRESET_SOUND,
	PRESET_INFO,
]

const actionsCamera = [
	PRESET_STUDIO_MODE,
	PRESET_RESET_CAMERA,
]

const presetsMain = [
	PRESET_RAINBOW,
	PRESET_RAIN,
	PRESET_COLOR_ONE,
	PRESET_COLOR_TWO,
	PRESET_COLOR_THREE,
];

const actionsSub = [
	PRESET_RESET_CONFIG,
	PRESET_FULL_SCREEN,
];

const actionsMain = [
	PRESET_RGB,
	PRESET_POWER,
	PRESET_OPEN,
	PRESET_INSPECT,
];

const getIconSize = initialProps => {
	return ({id}) => {
		if(ACTION_RGB === id) {
			return {...initialProps, width: 26}
		} else if(ACTION_RESET_CAMERA === id) {
			return {...initialProps, width: 26}
		} else if(ACTION_SOUND === id) {
			return {...initialProps, width: 24}
		}
		return initialProps;
	}
};

function ConfiguratorDesktop() {
	const {
		isStickyPanelCollapsed,
		streamStore,
		setStreamStore,
		getBundleTitleById,
		activeBundleId,
		toggleInteractionsPortal,
		isInteractionsPortalOpen,
		isConfiguratorAvailable,
	} = useContext(AppContext);
	const [isPresetsVisible, setIsPresetsVisible] = useState(false);
	const debounceStubRef = useRef(null);
	const emitter = useStreamActionsSingle();
	const {isDefaultView, isInspectView, isIsolateView} = useViewMode();
	const {
		sizeContainerRef,
		containerRef,
		videoRef,
		onPresetClickHandler,
		audioRef, /*Audio functionality*/
	} = useConfiguratorMount();
	const {
		isSoundOn,
		toggleSound,
	} = useSound();

	const makeInvisible = useCallback(function() {
		setIsPresetsVisible(false);
	}, [setIsPresetsVisible])
	const declineVisibilityHandler  = useCallback(() => {
		debounceStubRef.current?.cancel && debounceStubRef.current.cancel()
		debounceStubRef.current = null;
	}, [debounceStubRef])

	const debouncedTogglePresetsVisibility = useCallback(() => {
		setIsPresetsVisible(true);
		return debounce(makeInvisible, 750);
	}, [setIsPresetsVisible, makeInvisible])

	const onMouseLeavePresets = useCallback(() => {
		if(isPresetsVisible) {
			debounceStubRef.current = debouncedTogglePresetsVisibility();
			if(typeof debounceStubRef.current === 'function') {
				debounceStubRef.current();
			}
		}
	}, [isPresetsVisible, debounceStubRef, debouncedTogglePresetsVisibility])

	const eventsCollection = useMemo(() => {
		let _ec = new EventsCollection();

		_ec
			.add(ACTION_CALL_INFO, {
				onClick: toggleInteractionsPortal,
			})
			.add(ACTION_FULL_SCREEN, {
				onClick: () => openFullscreen(containerRef.current),
			})
			.add(ACTION_RGB, {
				onMouseEnter: () => {
					declineVisibilityHandler();
					setIsPresetsVisible(true);
				},
				onMouseLeave: onMouseLeavePresets,
			})
			.add([
				ACTION_RESET_CAMERA, ACTION_FULL_SCREEN, ACTION_RESET_CONFIG,
				ACTION_POWER, ACTION_OPEN, ACTION_INSPECT
			], {
				onMouseEnter: makeInvisible
			})
			.add([
				SYSTEM_ID_ICUE,
				ACTION_RESET_CAMERA,
				ACTION_RESET_CONFIG,
				ACTION_POWER,
				ACTION_OPEN,
				ACTION_STUDIO_MODE,
			], {
				onClick: [onPresetClickHandler],
			})
			.add([
				ACTION_INSPECT
			], {
				onClick: (el) => {
					/*Custom behavior*/
					if(isIsolateView) {
						if(emitter) {
							emitter.client.emitUIInteraction({
								[ACTION_ISOLATE_ITEM_DISABLED]: 1
							})
						}
					} else {
						if(emitter) {
							setStreamStore({
								[SYSTEM_ID_TOGGLE_DESK_CAMERA]: 0
							})
						}
						onPresetClickHandler(el);
					}
				}
			})
			.add([
				ACTION_SOUND,
			], {
				onClick: () => toggleSound(!isSoundOn)
			})

		return _ec;

	}, [
		isSoundOn,
		toggleInteractionsPortal,
		setIsPresetsVisible,
		onPresetClickHandler,
		declineVisibilityHandler,
		onMouseLeavePresets,
		makeInvisible,
	])

	const preparedInfoActionsMemoized = useMemo(() => {
		return actionsInfo.map(el => flow([
			applyProperties({
				[PROPERTY_VISIBLE]: {
					isNegation: true
				},
				[PROPERTY_ACTIVE]: {
					conditions: {
						[SYSTEM_ID_INFO]: () => isInteractionsPortalOpen,
						[SYSTEM_ID_SOUND]: () => isSoundOn,
					},
					getBy: 'action.id'
				},
			}),
			applyIconDataNew([
				getIconSize({width: 28, boxSize: 47}),
			]),
			applyEvents(eventsCollection, 'id')
		])(el))
	}, [
		isInteractionsPortalOpen,
		eventsCollection,
	]);

	const preparedCameraActionsMemoized = useMemo(() => {
		return actionsCamera.map(el => flow([
			applyProperties({
				[PROPERTY_VISIBLE]: {
					conditions: {
						[ACTION_STUDIO_MODE]: () => isIsolateView,
					},
					isNegation: true
				},
				[PROPERTY_ACTIVE]: {
					conditions: {
						[ACTION_STUDIO_MODE]: () => streamStore[SYSTEM_ID_TOGGLE_DESK_CAMERA],
					},
				}
			}),
			applyIconDataNew(getIconSize({width: 24, boxSize: 47})),
			applyEvents(eventsCollection, 'id')
		])(el))
	}, [
		streamStore,
		eventsCollection,
		streamStore[SYSTEM_ID_TOGGLE_DESK_CAMERA],
	]);

	const preparedPresetsMemoized = useMemo(() => {
		return presetsMain.map(el => flow([
			applyProperties({
				[PROPERTY_VISIBLE]: {
					isNegation: true
				},
				[PROPERTY_ACTIVE]: {
					conditions: {
						[SYSTEM_ID_ICUE]: el => streamStore[SYSTEM_ID_ICUE] === el.id,
					},
					getBy: 'action.id',
				},
				[PROPERTY_CLASS_NAME]: {
					conditions: {
						[EFFECT_ID_RAIN]: el => PREFIX_EFFECT + el.id,
						[EFFECT_ID_RAINBOW]: el => PREFIX_EFFECT + el.id,
					},
				}
			}),
			applyIconDataNew(() => ({width: 48, boxSize: 54})),
			applyEvents(eventsCollection, 'action.id'),
		])(el))
	}, [
		streamStore,
		eventsCollection,
	]);

	const preparedSubActionsMemoized = useMemo(() => {
		return actionsSub.map(el => flow([
			applyProp(PROPERTY_VISIBLE, {
				isNegation: true
			}),
			applyIconDataNew(getIconSize({width: 20, boxSize: 47})),
			applyEvents(eventsCollection, 'id'),
		])(el))
	}, [
		eventsCollection,
	]);

	const preparedMainActionsMemoized = useMemo(() => {
		return actionsMain.map(el => flow([
			applyProperties({
				[PROPERTY_VISIBLE]: {
					isNegation: true,
				},
				[PROPERTY_ACTIVE]: {
					conditions: {
						[SYSTEM_ID_RGB]: () => isPresetsVisible,
						...[
							SYSTEM_ID_TOGGLE_POWER,
							SYSTEM_ID_TOGGLE_DOOR,
						].reduce((acc, id) => {
							acc[id] = (el) => !!streamStore[el.action.id];
							return acc;
						}, {}),
						[SYSTEM_ID_TOGGLE_INSPECT]: (el) => {
							return (!!streamStore[el.action.id] && isInspectView) || isIsolateView
						},
					},
					getBy: 'action.id',
				},
			}),
			applyIconDataNew(getIconSize({width: 24, boxSize: 47})),
			applyEvents(eventsCollection, 'id'),
		])(el))
	}, [
		streamStore,
		isPresetsVisible,
		eventsCollection,
		isInspectView,
	]);

	const bundleTitle = useMemo(getBundleTitleById, [getBundleTitleById, activeBundleId]);

	/*@TODO Does not work as intended*/
	const isVisibleTitle = useMemo(() => {
		return (!isStickyPanelCollapsed && isDefaultView && bundleTitle && streamStore[INTRO_MOVIE_FINISHED])
	}, [
		isStickyPanelCollapsed,
		isDefaultView,
		bundleTitle,
		streamStore[INTRO_MOVIE_FINISHED],
	])

	return (
		<ConfiguratorHolder
			sizeContainerRef={sizeContainerRef}
			containerRef={containerRef}
			videoRef={videoRef}
			audioRef={audioRef}
		>
			<>
				<CSSTransition
					in={isPresetsVisible}
					timeout={500}
				>
					<ActionsList
						onMouseEnter={declineVisibilityHandler}
						onMouseLeave={onMouseLeavePresets}
						className="presets-main"
						items={preparedPresetsMemoized}
						type={TYPE_SECONDARY}
						reverse={true}
					>
						<IconClose
							onClick={makeInvisible}
							className={'close-presets-main'}
						/>
					</ActionsList>
				</CSSTransition>

				<ActionsList
					className={classNames('actions-info', {
						'hidden': !isConfiguratorAvailable
					})}
					items={preparedInfoActionsMemoized}
					offset={8}
					axis={AXIS_VERTICAL}
				/>

				<ActionsList
					className={classNames('actions-sub', {
						'hidden': !isConfiguratorAvailable
					})}
					items={preparedSubActionsMemoized}
					offset={8}
					axis={AXIS_VERTICAL}
					reverse={true}
				/>

				<ActionsList
					className={classNames('actions-camera', {
						'hidden': !isConfiguratorAvailable
					})}
					items={preparedCameraActionsMemoized}
					offset={0}
					reverse={true}
				/>

				<ActionsList
					className={classNames('actions-main', {
						'hidden': !isConfiguratorAvailable
					})}
					items={preparedMainActionsMemoized}
				/>

				<CSSTransition
					in={isVisibleTitle}
					classNames="fade"
					appear={true}
					timeout={700}
				>
					<div className="title-hero-container">
						<TitledName className={classNames('title-hero', {
							'offset': !isStickyPanelCollapsed
						})} level={1} title={bundleTitle} />
					</div>
				</CSSTransition>

			</>
		</ConfiguratorHolder>
	)
}

export default ConfiguratorDesktop;
